import React from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import stripMarkdown from 'strip-markdown';
import { useMessagesStore } from '@/hooks/useMessagesStore';
import { useWidgetConfig } from '@/hooks/useWidgetConfig';
import { Message } from '@/types/message.types';
import { formatMessageTime } from '@/utils/message';

export interface LatestMessageProps {
  onStartChat: () => void;
}

const LatestMessage: React.FC<LatestMessageProps> = ({ onStartChat }) => {
  const { t } = useTranslation('common');
  const { branding } = useWidgetConfig();
  const lastMessage = useMessagesStore(
    (state) => state.messages[state.messages.length - 1],
  );

  const agentDisplayName = branding.senderDisplayName;

  // Truncate message content for preview
  const getMessagePreview = (message: Message) => {
    if (message.messageType === 'file') {
      return '(media)';
    }

    return (
      <Markdown
        remarkPlugins={[stripMarkdown]}
        components={{
          p: ({ children }) => (
            <span className="sf:text-body1">{children}</span>
          ),
        }}
      >
        {message.messageContent}
      </Markdown>
    );
  };

  if (!lastMessage) {
    return (
      <div className="sf:space-y-4">
        <p className="sf:text-gray-900 sf:text-sm sf:leading-5 sf:font-semibold">
          {t('landingPage.instantAnswersDescription')}
        </p>

        <button
          onClick={onStartChat}
          className="sf:bg-widget-button-color sf:text-widget-button-text-color sf:font-semibold sf:text-sm sf:py-2.5 sf:px-6 sf:rounded-full sf:flex sf:items-center sf:justify-center sf:gap-2 sf:transition-all sf:hover:opacity-60 sf:cursor-pointer"
        >
          <span>{t('landingPage.startChatButton')}</span>
          <svg
            className="sf:w-4 sf:h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.6}
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="sf:flex sf:flex-col sf:gap-3">
      <div className="sf:flex sf:flex-col sf:gap-1">
        {/* Timestamp */}
        <span className="sf:text-body3 sf:text-[#3C3F44]">
          {formatMessageTime(lastMessage.timestamp)}
        </span>

        {/* message preview */}
        <p className="sf:line-clamp-2">
          <span className="sf:text-body1 sf:font-semibold">
            {lastMessage.isSentFromSleekflow
              ? agentDisplayName
              : t('latestMessage.you')}
            :
          </span>{' '}
          <span className="sf:text-body1 sf:whitespace-pre-line">
            {getMessagePreview(lastMessage)}
          </span>
        </p>
      </div>

      {/* Back to conversation button */}
      <button
        onClick={onStartChat}
        className="sf:bg-widget-button-color sf:text-widget-button-text-color sf:font-semibold sf:py-2.5 sf:px-4 sf:rounded-full sf:flex sf:items-center sf:justify-center sf:gap-2 sf:transition-all sf:hover:opacity-80 sf:cursor-pointer sf:text-sm sf:w-fit"
      >
        <span>
          {t('latestMessage.backToConversation', 'Back to conversation')}
        </span>
        <svg
          className="sf:w-4 sf:h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.6}
            d="M13 7l5 5m0 0l-5 5m5-5H6"
          />
        </svg>
      </button>
    </div>
  );
};

export default LatestMessage;
